import { ProviderDetail } from "../types/provider";
import { EditFormData } from "../types/providerDetail";
import {
  BUSINESS_TYPE_DEFINITIONS,
  BUSINESS_TYPE_NORMALIZATION,
} from "../generated/careTaxonomy";
import { getCountyFromAddress } from "../constants/mnCounties";
import {
  formatFundingAcceptedForDisplay,
  formatProviderPaymentAcceptedDisplay,
} from "../constants/fundingTaxonomy";
import { mapServicesToUniqueDisplay } from "./serviceMappings";
import { getStateName } from "./stateAbbreviations";
import { getNormalizedProviderCapabilities } from "./providerCapabilityFields";

/** Minimal provider shape needed for county display (avoids coupling to full ProviderDetail). */
export type ProviderCountyInput = Pick<
  ProviderDetail,
  "address" | "city" | "state" | "zip_code" | "county_name"
>;

/**
 * Single source of truth for county *name* display on provider detail (tags use this plus
 * optional `county_code` for "FIPS …" in `ProviderTagsSection`).
 * Prefers backend `county_name`. If missing, uses MN ZIP→county mapping **only when
 * `state === "MN"`** (`mnCounties`). Otherwise falls back to parsing "County" from address
 * or `"City, ST"` / `"Unknown Location"` — never defaults to Minneapolis.
 */
export const getDisplayCounty = (provider: ProviderCountyInput | null | undefined): string => {
  if (!provider) return "N/A";
  return getCountyFromAddress(
    provider.address ?? "",
    provider.city ?? "",
    provider.state ?? "",
    provider.zip_code,
    provider.county_name
  );
};

type BusinessTypeDefinition = (typeof BUSINESS_TYPE_DEFINITIONS)[number];

const BUSINESS_TYPE_BY_KEY = new Map<string, BusinessTypeDefinition>(
  BUSINESS_TYPE_DEFINITIONS.map((entry) => [entry.key, entry])
);

function resolveBusinessTypeKey(raw: string): string {
  const key = raw.trim().toLowerCase().replace(/\s+/g, "_");
  if (!key) return key;
  if (BUSINESS_TYPE_BY_KEY.has(key)) return key;
  const collapsed = key.replace(/_/g, "");
  return (
    BUSINESS_TYPE_NORMALIZATION[key] ??
    BUSINESS_TYPE_NORMALIZATION[collapsed] ??
    key
  );
}

/**
 * Format a stored business_type key for cards, headers, and profile pages.
 * Prefers taxonomy searchFilterLabel for the storage key so inventory identity
 * stays distinct (e.g. home_care → "Home Care Agency").
 */
export const formatBusinessType = (type: string | undefined | null): string => {
  if (!type) return "";
  const key = resolveBusinessTypeKey(type);
  const entry = BUSINESS_TYPE_BY_KEY.get(key);
  if (entry?.searchFilterLabel) {
    return entry.searchFilterLabel;
  }
  if (entry?.fullLabel) {
    return entry.fullLabel;
  }
  return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export const getBusinessTypeDisplay = (businessType: string): string =>
  formatBusinessType(businessType);

/** Profile subtitle under the business name — only when a custom tagline exists. */
export function getProviderProfileSubtitle(
  provider: Pick<ProviderDetail, "tagline">
): string | null {
  const tagline = provider.tagline?.trim();
  return tagline || null;
}

/** Fields needed to reconcile stale Minnesota-ingestion boilerplate on non-MN rows. */
export type ProviderDescriptionDisplayInput = Pick<
  ProviderDetail,
  "description" | "state" | "city" | "business_type"
>;

/**
 * Show stored description unless it is clearly Minnesota MDH or Wisconsin DHS
 * template text while `state` is not that state (ZIP canonicalization / bad merges).
 */
export const getProviderDescriptionForDisplay = (
  provider: ProviderDescriptionDisplayInput | null | undefined
): string => {
  if (!provider) return "No description provided";
  const raw = (provider.description || "").trim();
  if (!raw) return "No description provided";
  const st = (provider.state || "").trim().toUpperCase();

  const genericFromLocation = (): string => {
    const city = (provider.city || "").trim() || "Unknown city";
    const stateName = getStateName(st) || st || "Unknown state";
    const place = `${city}, ${stateName}`;
    const typeLabel = getBusinessTypeDisplay(
      provider.business_type || ""
    ).toLowerCase();
    return `Licensed ${typeLabel} provider in ${place}.`;
  };

  if (st === "MN") {
    // Still allow WI text on MN row? Unlikely; if both wrong, generic is safer.
    if (/\bWisconsin\s+licensed\b/i.test(raw) || /dhs\.wisconsin\.gov/i.test(raw)) {
      return genericFromLocation();
    }
    return raw;
  }
  if (st === "WI") {
    if (
      /Minnesota Department of Health/i.test(raw) ||
      /Minnesota Department of\b/i.test(raw) ||
      (/, Minnesota\.?\s/.test(raw) && /Licensed/i.test(raw))
    ) {
      return genericFromLocation();
    }
    return raw;
  }

  const looksStaleMn =
    /Minnesota Department of Health/i.test(raw) ||
    /Minnesota Department of\b/i.test(raw) ||
    (/, Minnesota\.?\s/.test(raw) && /Licensed/i.test(raw));
  const looksStaleWi =
    /\bWisconsin\s+licensed\b/i.test(raw) ||
    /^Wisconsin\s+[\w\s]+\s*:\s*\S/m.test(raw) ||
    /dhs\.wisconsin\.gov/i.test(raw);

  if (looksStaleMn || looksStaleWi) return genericFromLocation();
  return raw;
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Parse JSON field safely
export const parseJsonField = (jsonStr?: string): string[] => {
  if (!jsonStr) return [];
  try {
    const parsed = JSON.parse(jsonStr);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [jsonStr];
  }
};

// Format disability support services from JSON string
export const formatDisabilitySupportServices = (
  servicesStr?: string
): string[] => {
  return parseJsonField(servicesStr);
};

// Format specializations from JSON string
export const formatSpecializations = (
  specializationsStr?: string
): string[] => {
  return parseJsonField(specializationsStr);
};

// Format handicap accessibility features from JSON string
export const formatHandicapAccessibility = (featuresStr?: string): string[] => {
  return parseJsonField(featuresStr);
};

export const resolveHomesWheelchairAccessible = (
  provider: Pick<
    ProviderDetail,
    | "disability_support_services"
    | "handicap_accessibility"
    | "accessibility_features"
    | "homes_wheelchair_accessible"
  >
): boolean =>
  getNormalizedProviderCapabilities(provider).homes_wheelchair_accessible;

// Format safety security features from JSON string
export const formatSafetySecurity = (featuresStr?: string): string[] => {
  return parseJsonField(featuresStr);
};

// Format languages spoken from JSON string
export const formatLanguagesSpoken = (languagesStr?: string): string[] => {
  return parseJsonField(languagesStr);
};

// Format funding accepted from JSON string (taxonomy labels, not raw codes)
export const formatFundingAccepted = (fundingStr?: string): string[] => {
  return formatFundingAcceptedForDisplay(fundingStr, "provider");
};

export { resolveProviderAgeGroups } from "./ageGroups";

const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

function dayKeyToLabel(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// Format hours of operation from JSON string.
// Supports: (1) object format from edit form { monday: { open, close, closed }, ... };
//           (2) array format { day, hours }[] for backward compatibility.
export const formatHoursOfOperation = (
  hoursStr?: string
): { day: string; hours: string }[] => {
  if (!hoursStr) return [];
  try {
    const hours = JSON.parse(hoursStr);
    if (Array.isArray(hours)) {
      return hours.map((h) => ({ day: h.day || "", hours: h.hours || "" }));
    }
    if (hours && typeof hours === "object" && !Array.isArray(hours)) {
      return DAY_KEYS.map((key) => {
        const dayLabel = dayKeyToLabel(key);
        const slot = hours[key];
        if (!slot || typeof slot !== "object") {
          return { day: dayLabel, hours: "" };
        }
        if (slot.closed) {
          return { day: dayLabel, hours: "Closed" };
        }
        const open = (slot.open || "").trim();
        const close = (slot.close || "").trim();
        if (!open && !close) return { day: dayLabel, hours: "" };
        const is24h =
          (open === "12:00 AM" && close === "11:59 PM") ||
          (open === "00:00" && close === "23:59");
        if (is24h) {
          return { day: dayLabel, hours: "24 hours" };
        }
        return { day: dayLabel, hours: `${open} – ${close}`.trim() };
      }).filter((row) => row.hours !== "");
    }
    return [];
  } catch {
    return [];
  }
};

export const formatServices = (servicesStr?: string): string[] => {
  const rawServices = servicesStr?.trim();
  if (!rawServices) return [];

  let parsedServices: unknown;
  try {
    parsedServices = JSON.parse(rawServices);
  } catch {
    parsedServices = rawServices.split(",");
  }

  const serviceArray = Array.isArray(parsedServices)
    ? parsedServices
    : typeof parsedServices === "string"
      ? [parsedServices]
      : [];
  const normalizedServices = serviceArray
    .filter((service) => service != null)
    .map((service) => String(service).trim())
    .filter(Boolean);

  return mapServicesToUniqueDisplay(normalizedServices);
};

// Format accessibility features from JSON string
export const formatAccessibilityFeatures = (featuresStr?: string): string[] => {
  if (!featuresStr) return [];
  try {
    const features = JSON.parse(featuresStr);
    const featureArray = Array.isArray(features) ? features : [featuresStr];

    // Remove duplicates while preserving order
    const uniqueFeatures = featureArray.filter(
      (feature, index, arr) => arr.indexOf(feature) === index
    );

    return uniqueFeatures;
  } catch {
    return [featuresStr];
  }
};

// Serialize hours of operation from form data to JSON string
export const serializeHoursOfOperation = (formData: EditFormData): string => {
  const hours = {
    monday: {
      open: formData.monday_open || "",
      close: formData.monday_close || "",
      closed: formData.monday_closed || false,
    },
    tuesday: {
      open: formData.tuesday_open || "",
      close: formData.tuesday_close || "",
      closed: formData.tuesday_closed || false,
    },
    wednesday: {
      open: formData.wednesday_open || "",
      close: formData.wednesday_close || "",
      closed: formData.wednesday_closed || false,
    },
    thursday: {
      open: formData.thursday_open || "",
      close: formData.thursday_close || "",
      closed: formData.thursday_closed || false,
    },
    friday: {
      open: formData.friday_open || "",
      close: formData.friday_close || "",
      closed: formData.friday_closed || false,
    },
    saturday: {
      open: formData.saturday_open || "",
      close: formData.saturday_close || "",
      closed: formData.saturday_closed || false,
    },
    sunday: {
      open: formData.sunday_open || "",
      close: formData.sunday_close || "",
      closed: formData.sunday_closed || false,
    },
  };

  return JSON.stringify(hours);
};

// Deserialize hours of operation from JSON string to form data
export const deserializeHoursOfOperation = (
  hoursStr?: string
): Partial<EditFormData> => {
  if (!hoursStr) return {};

  try {
    const hours = JSON.parse(hoursStr);
    return {
      monday_open: hours.monday?.open || "",
      monday_close: hours.monday?.close || "",
      monday_closed: hours.monday?.closed || false,
      tuesday_open: hours.tuesday?.open || "",
      tuesday_close: hours.tuesday?.close || "",
      tuesday_closed: hours.tuesday?.closed || false,
      wednesday_open: hours.wednesday?.open || "",
      wednesday_close: hours.wednesday?.close || "",
      wednesday_closed: hours.wednesday?.closed || false,
      thursday_open: hours.thursday?.open || "",
      thursday_close: hours.thursday?.close || "",
      thursday_closed: hours.thursday?.closed || false,
      friday_open: hours.friday?.open || "",
      friday_close: hours.friday?.close || "",
      friday_closed: hours.friday?.closed || false,
      saturday_open: hours.saturday?.open || "",
      saturday_close: hours.saturday?.close || "",
      saturday_closed: hours.saturday?.closed || false,
      sunday_open: hours.sunday?.open || "",
      sunday_close: hours.sunday?.close || "",
      sunday_closed: hours.sunday?.closed || false,
    };
  } catch {
    return {};
  }
};

// Parse hours of operation from text string
export const parseHoursOfOperation = (
  hoursStr?: string
): Partial<EditFormData> => {
  const defaultHours: Partial<EditFormData> = {
    monday_open: "",
    monday_close: "",
    monday_closed: false,
    tuesday_open: "",
    tuesday_close: "",
    tuesday_closed: false,
    wednesday_open: "",
    wednesday_close: "",
    wednesday_closed: false,
    thursday_open: "",
    thursday_close: "",
    thursday_closed: false,
    friday_open: "",
    friday_close: "",
    friday_closed: false,
    saturday_open: "",
    saturday_close: "",
    saturday_closed: false,
    sunday_open: "",
    sunday_close: "",
    sunday_closed: false,
  };

  if (!hoursStr) return defaultHours;

  // Try to parse structured hours (e.g., "Monday: 09:00-17:00")
  const dayPattern =
    /(monday|tuesday|wednesday|thursday|friday|saturday|sunday):\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/gi;
  const matches = hoursStr.match(dayPattern);

  if (matches) {
    matches.forEach((match) => {
      const [, day, openTime, closeTime] =
        match.match(
          /(monday|tuesday|wednesday|thursday|friday|saturday|sunday):\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/i
        ) || [];
      if (day && openTime && closeTime) {
        const dayKey = day.toLowerCase();
        (defaultHours as any)[`${dayKey}_open`] = openTime;
        (defaultHours as any)[`${dayKey}_close`] = closeTime;
        (defaultHours as any)[`${dayKey}_closed`] = false;
      }
    });
  }

  return defaultHours;
};

export const formatInsurance = (provider: ProviderDetail): string[] => {
  return formatProviderPaymentAcceptedDisplay(provider, "search");
};

export const parseArrayField = (
  field: string | string[] | undefined
): string[] => {
  if (!field) return [];

  // If it's already an array, return it
  if (Array.isArray(field)) {
    return field;
  }

  // If it's a string, try to parse it as JSON
  if (typeof field === "string") {
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [field];
    } catch (error) {
      // If JSON parsing fails, treat it as a comma-separated string
      return field
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }
  }

  return [];
};

export const getStarRating = (rating: number): string => {
  const stars =
    "⭐".repeat(Math.floor(rating)) + "☆".repeat(5 - Math.floor(rating));
  return stars;
};
